import React, { useRef, useState } from 'react';
import { Upload, X, Video, AlertTriangle, Loader2 } from 'lucide-react';
import apiService from '../../../services/api';
import './SoraGeneration.css';

const MAX_DESC = 2000;

const MODELS = [
  { value: 'tongyiwanxiang', label: '自研' },
];

const RATIOS = [
  { value: '9:16',  label: '9:16 竖屏' },
  { value: '16:9',  label: '16:9 横屏' },
];

const DURATIONS = [
  { value: '15', label: '15秒' },
];

const SoraGeneration = ({
  data,
  onChange,
  onGenerate,
  isGenerating,
  isButtonDisabled,
}) => {
  const sora = data.sora || {};
  const imgInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const set = (field, value) => onChange('sora', { ...sora, [field]: value });

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setUploadError('仅支持 JPG、PNG 格式');
      return;
    }

    // Validate file size (30MB)
    if (file.size > 30 * 1024 * 1024) {
      setUploadError('文件大小不能超过 30MB');
      return;
    }

    setUploadError(null);

    // Store file with a preview URL for display immediately
    const previewUrl = URL.createObjectURL(file);
    set('uploadedFile', { file, previewUrl, name: file.name, uploading: true });

    // Upload the file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await apiService.uploadImageForSora(formData);

      // 后端返回 fileKey
      if (result?.fileKey) {
        set('uploadedFile', { file, previewUrl, name: file.name, uploadedId: result.fileKey, uploading: false });
      } else {
        throw new Error(result?.message || '上传失败');
      }
    } catch (err) {
      setUploadError(err.message || '上传失败，请重试');
      set('uploadedFile', { file, previewUrl, name: file.name, uploadError: err.message });
    } finally {
      setUploading(false);
    }

    e.target.value = '';
  };

  const handleImageRemove = () => {
    if (sora.uploadedFile?.previewUrl) {
      URL.revokeObjectURL(sora.uploadedFile.previewUrl);
    }
    set('uploadedFile', null);
    setUploadError(null);
  };

  const descLen = (sora.description || '').length;
  const isValid = descLen > 0;

  return (
    <div className="sg-page">

      {/* ── Step 1: 选择模型 ── */}
      <div className="sg-section">
        <p className="sg-section-title">选择模型 <span className="sg-required">*</span></p>
        <div className="sg-radio-group">
          {MODELS.map(m => (
            <label
              key={m.value}
              className={`sg-radio-label${(sora.model || 'tongyiwanxiang') === m.value ? ' active' : ''}`}
            >
              <input
                type="radio"
                name="sora-model"
                value={m.value}
                checked={(sora.model || 'tongyiwanxiang') === m.value}
                onChange={() => set('model', m.value)}
              />
              <span className="sg-radio-dot" />
              {m.label}
            </label>
          ))}
        </div>
      </div>

      {/* ── Step 2: 选择引导图片 ── */}
      <div className="sg-section">
        <p className="sg-section-title">选择引导图片</p>

        <table className="sg-info-table">
          <tbody>
            <tr>
              <td>支持格式</td>
              <td>JPG、PNG</td>
            </tr>
            <tr>
              <td>最大大小</td>
              <td>30MB</td>
            </tr>
            <tr>
              <td>用途</td>
              <td>引导 AI 按照图片风格或内容生成视频</td>
            </tr>
          </tbody>
        </table>

        <div className="sg-warning">
          <AlertTriangle size={15} className="sg-warning-icon" />
          禁止上传含有人脸的图片，违规图片将被过滤
        </div>

        {sora.uploadedFile ? (
          <div className="sg-uploaded-img">
            <img
              className="sg-uploaded-thumb"
              src={sora.uploadedFile.previewUrl}
              alt={sora.uploadedFile.name}
            />
            <span className="sg-uploaded-name">
              {sora.uploadedFile.name}
              {sora.uploadedFile.uploading && ' (上传中...)'}
              {sora.uploadedFile.uploadError && ` - ${sora.uploadedFile.uploadError}`}
            </span>
            <button className="sg-uploaded-remove" onClick={handleImageRemove} title="移除图片">
              <X size={14} />
            </button>
          </div>
        ) : (
          <label className="sg-upload-zone">
            <input
              ref={imgInputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleImagePick}
              disabled={uploading}
            />
            {uploading ? <Loader2 size={16} className="sg-spin" /> : <Upload size={16} />}
            {uploading ? '上传中...' : '选择文件'}
          </label>
        )}

        {uploadError && (
          <div className="sg-error">{uploadError}</div>
        )}
      </div>

      {/* ── Step 3: 输入视频描述 ── */}
      <div className="sg-section">
        <p className="sg-section-title">输入视频描述 <span className="sg-required">*</span></p>

        <div className="sg-tip-box">
          <strong>提示词技巧：</strong>使用具体的场景描述、光线、摄像机角度和动作细节，
          例如"从低角度拍摄，阳光穿透树叶，慢动作"，效果更佳。避免使用含糊词语。
        </div>

        <div className="sg-textarea-wrap">
          <textarea
            className="sg-textarea"
            rows={5}
            maxLength={MAX_DESC}
            placeholder="请详细描述你想要生成的视频内容、风格、场景等..."
            value={sora.description || ''}
            onChange={(e) => set('description', e.target.value)}
          />
          <div className="sg-char-count">{descLen}/{MAX_DESC}</div>
        </div>
      </div>

      {/* ── Step 4: 视频比例 ── */}
      <div className="sg-section">
        <p className="sg-section-title">视频比例</p>
        <div className="sg-radio-group">
          {RATIOS.map(r => (
            <label
              key={r.value}
              className={`sg-radio-label${(sora.ratio || '9:16') === r.value ? ' active' : ''}`}
            >
              <input
                type="radio"
                name="sora-ratio"
                value={r.value}
                checked={(sora.ratio || '9:16') === r.value}
                onChange={() => set('ratio', r.value)}
              />
              <span className="sg-radio-dot" />
              {r.label}
            </label>
          ))}
        </div>
      </div>

      {/* ── Step 5: 视频时长 ── */}
      <div className="sg-section">
        <p className="sg-section-title">视频时长</p>
        <div className="sg-radio-group">
          {DURATIONS.map(d => (
            <label
              key={d.value}
              className={`sg-radio-label${(sora.duration || '15') === d.value ? ' active' : ''}`}
            >
              <input
                type="radio"
                name="sora-duration"
                value={d.value}
                checked={(sora.duration || '15') === d.value}
                onChange={() => set('duration', d.value)}
              />
              <span className="sg-radio-dot" />
              {d.label}
            </label>
          ))}
        </div>
      </div>

      {/* ── Generate ── */}
      <div className="sg-generate">
        <button
          className="sg-generate-btn"
          onClick={onGenerate}
          disabled={isButtonDisabled}
        >
          {isGenerating ? (
            <><div className="sg-spinner" />生成中，请稍候...</>
          ) : (
            <><Video size={16} />开始生成</>
          )}
        </button>
      </div>

    </div>
  );
};

export default SoraGeneration;
